import { Controller } from '@hotwired/stimulus';
import { getMeta } from '../helpers';
import Axios from '../axios';

export default class extends Controller {
	static get targets () {
		return ['choicePanel', 'loginPanel', 'registerPanel',
			'loginEmail', 'loginPassword', 'loginError', 'loginMessage', 'loginSubmit', 'resetSuccess', 'forgotSubmit',
			'registerEmail', 'registerPassword', 'registerPasswordRepeat', 'registerError', 'registerSubmit'];
	}

	connect() {
		this._lang = document.documentElement.lang.split('-')[0];
		this._pending = null;
	}

	open(event) {
		const element = event.currentTarget;
		const modal = document.querySelector('#checkoutChoiceModal');

		if (!modal) {
			return;
		}

		event.preventDefault();

		let pending;

		if (element.dataset.redirectAfter && !element.dataset.planId) {
			pending = {
				redirect: element.dataset.redirectAfter
			};
		} else {
			const payload = element.dataset.clubId ? {
				plan_id: element.dataset.planId,
				club_id: element.dataset.clubId,
				pid: element.dataset.pid,
				track: element.dataset.track || ''
			} : {
				id: element.dataset.planId,
				pid: element.dataset.pid,
				track: element.dataset.track || ''
			};

			pending = {
				route: element.dataset.route,
				payload: payload
			};
		}

		const modalController = this.application.getControllerForElementAndIdentifier(modal, 'checkout-choice');

		if (modalController && modalController !== this) {
			modalController.begin(pending);
		} else if (modalController === this) {
			this.begin(pending);
		}
	}

	begin(pending) {
		this._pending = pending;

		this.showChoice();

		const modalController = this.application.getControllerForElementAndIdentifier(this.element, 'modal');

		if (modalController) {
			modalController.showModal(this.element);
		}
	}

	showChoice() {
		this.showPanel('choicePanel');
	}

	showLogin() {
		this.showPanel('loginPanel');
	}

	showRegister() {
		this.showPanel('registerPanel');
	}

	showPanel(panel) {
		['choicePanel', 'loginPanel', 'registerPanel'].forEach(name => {
			this[`${name}Target`].classList.toggle('d-none', name !== panel);
		});

		this.clearMessages();
	}

	async submitLogin() {
		this.clearMessages();

		const submit = this.loginSubmitTarget;
		submit.classList.add('loading');

		let response;

		try {
			response = await this.handlePostSubmit(`${this.element.dataset.loginRoute}?rest_language=${this._lang}`, {
				email: this.loginEmailTarget.value,
				password: this.loginPasswordTarget.value
			});
			response = response.data.data;
		} catch (error) {
			response = this.errorResponse(error);
		}

		submit.classList.remove('loading');

		if (response && response.errors) {
			this.showFieldErrors(this.loginPanelTarget, response.errors, this.loginErrorTarget);
		} else if (response && response.general_error) {
			this.showGeneralError(this.loginErrorTarget, response.general_error);
		} else if (response && response.success) {
			this.finalize(submit);
		} else {
			this.showGeneralError(this.loginErrorTarget, getMeta('global_axios_validator_error'));
		}
	}

	async forgotPassword() {
		this.clearMessages();

		const submit = this.forgotSubmitTarget;
		submit.classList.add('loading');

		let response;

		try {
			response = await this.handlePostSubmit(`${this.element.dataset.resetRoute}?rest_language=${this._lang}`, {
				email: this.loginEmailTarget.value
			});
			response = response.data.data;
		} catch (error) {
			response = this.errorResponse(error);
		}

		submit.classList.remove('loading');

		if (response && response.general_success) {
			this.resetSuccessTarget.classList.remove('d-none');
		} else if (response && response.general_error) {
			this.showGeneralError(this.loginErrorTarget, response.general_error);
		} else if (response && response.errors) {
			this.showFieldErrors(this.loginPanelTarget, response.errors, this.loginErrorTarget);
		} else if (response && response.message) {
			this.showGeneralError(this.loginErrorTarget, response.message);
		} else {
			this.showGeneralError(this.loginErrorTarget, getMeta('global_axios_validator_error'));
		}
	}

	async submitRegister() {
		this.clearMessages();

		if (this.registerPasswordTarget.value !== this.registerPasswordRepeatTarget.value) {
			this.registerPasswordRepeatTarget.classList.add('invalid');
			const messageNode = document.createElement('span');
			messageNode.classList.add('input-error');
			messageNode.innerHTML = this.registerPasswordRepeatTarget.dataset.mismatchMessage;
			this.registerPasswordRepeatTarget.parentNode.appendChild(messageNode);
			return;
		}

		const submit = this.registerSubmitTarget;
		submit.classList.add('loading');

		let response;

		try {
			response = await this.handlePostSubmit(`${this.element.dataset.registerRoute}?rest_language=${this._lang}`, {
				email: this.registerEmailTarget.value,
				password: this.registerPasswordTarget.value
			});
			response = response.data.data;
		} catch (error) {
			response = this.errorResponse(error);
		}

		submit.classList.remove('loading');

		if (response && response.errors) {
			this.showFieldErrors(this.registerPanelTarget, response.errors, this.registerErrorTarget);
		} else if (response && response.general_error) {
			this.showGeneralError(this.registerErrorTarget, response.general_error);
		} else if (response && response.action === 'email_exists') {
			this.loginEmailTarget.value = this.registerEmailTarget.value;
			this.showPanel('loginPanel');
			this.showGeneralError(this.loginMessageTarget, response.action_data.message);
		} else if (response && response.success) {
			this.finalize(submit);
		} else {
			this.showGeneralError(this.registerErrorTarget, getMeta('global_axios_validator_error'));
		}
	}

	async finalize(button) {
		if (!this._pending) {
			return;
		}

		if (this._pending.redirect) {
			window.location.href = this._pending.redirect;
			return;
		}

		button.classList.add('loading');

		let response;

		try {
			response = await Axios.post(`${this._pending.route}?rest_language=${this._lang}`, this._pending.payload);
			response = response.data.data;
		} catch (error) {
			response = this.errorResponse(error);
		}

		button.classList.remove('loading');

		if (response && response.redirect) {
			window.location.href = response.redirect;
		} else if (response && response.modal) {
			const modalElement = document.querySelector(`#planSurveyModal${response.modal_id}`);

			if (modalElement) {
				modalElement.remove();
			}

			document.body.insertAdjacentHTML('beforeend', response.modal);

			const choiceModalController = this.application.getControllerForElementAndIdentifier(this.element, 'modal');

			if (choiceModalController) {
				choiceModalController.hideModal(this.element);
			}

			setTimeout(() => {
				const modalController = this.application.getControllerForElementAndIdentifier(document.querySelector(`#planSurveyModal${response.modal_id}`), 'modal');
				if (modalController) {
					modalController.showModal(document.querySelector(`#planSurveyModal${response.modal_id}`));
				}
			}, 200);
		} else if (response && response.general_error) {
			const errorTarget = this.registerPanelTarget.classList.contains('d-none')
				? this.loginErrorTarget
				: this.registerErrorTarget;
			this.showGeneralError(errorTarget, response.general_error);
		}
	}

	showFieldErrors(panel, errors, generalErrorTarget) {
		let shown = false;

		Object.keys(errors).forEach(key => {
			const input = panel.querySelector(`[data-choice-field="${key}"]`);

			if (input) {
				input.classList.add('invalid');
				const messageNode = document.createElement('span');
				messageNode.classList.add('input-error');
				messageNode.innerHTML = errors[key];
				input.parentNode.appendChild(messageNode);
				shown = true;
			}
		});

		if (!shown) {
			const firstKey = Object.keys(errors)[0];
			this.showGeneralError(generalErrorTarget, errors[firstKey]);
		}
	}

	showGeneralError(target, message) {
		target.innerHTML = message;
		target.classList.remove('d-none');
	}

	clearMessages() {
		this.element.querySelectorAll('.input-error').forEach(error => {
			error.remove();
		});
		this.element.querySelectorAll('.invalid').forEach(input => {
			input.classList.remove('invalid');
		});
		['loginError', 'loginMessage', 'registerError', 'resetSuccess'].forEach(name => {
			this[`${name}Target`].classList.add('d-none');
		});
	}

	errorResponse(error) {
		if (error.response && error.response.data) {
			return error.response.data.data || error.response.data;
		}

		return null;
	}

	handlePostSubmit(url, data, config) {
		if (window.grecaptcha) {
			return new Promise((resolve) => {
				window.grecaptcha.ready(() => {
					resolve(window.grecaptcha.execute(document.querySelector('meta[name="global_recaptcha_site_key"]').content, { action: 'submit' }));
				});
			}).then(recaptchaToken => {
				data.recaptcha = recaptchaToken;

				return Axios.post(url, data, config);
			});
		}

		return Axios.post(url, data, config);
	}
}
